import { useEffect, useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { baseColor } from "../..";

export type PaginationProps = {
  // 総数
  pageCount: number;
  // 表示するページ数
  onChange: (page: number) => void;
  // ページネーションで表示するセル数
  cellNumber: number;
  className?: string;
  color?: "blue" | "red" | "green" | "yellow" | "indigo";
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  currentPage: number;
  sliceStart: number;
  setSliceStart: React.Dispatch<React.SetStateAction<number>>;
  sliceEnd: number;
  setSliceEnd: React.Dispatch<React.SetStateAction<number>>;
  firstPageFlg?: boolean;
};

const style = {
  color: {
    blue: `border-blue-500 bg-blue-50 text-blue-600 z-30`,
    green: `border-green-500 bg-green-50 text-green-600 z-30`,
    red: `border-red-500 bg-red-50 text-red-600 z-30`,
    yellow: `border-yellow-500 bg-yellow-50 text-yellow-600 z-30`,
    indigo: `"border-indigo-500 bg-indigo-50 text-indigo-600 z-30" `,
  },
  base: `relative inline-flex items-center border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20`
};

export const Pagination = ({
  pageCount,
  className,
  onChange,
  cellNumber,
  color = baseColor,
  setCurrentPage,
  currentPage,
  sliceStart,
  setSliceStart,
  sliceEnd,
  setSliceEnd,
  firstPageFlg,
}: PaginationProps) => {
  const totalPage: number = pageCount;
  const minCellNumber = cellNumber > totalPage ? totalPage : cellNumber;

  const isFirstRender = useRef(true);
  const [disabledPrev, setDisabledPrev] = useState(true);
  const [disabledNext, setDisabledNext] = useState(false);

  // 検索時にページャを更新
  useEffect(() => {
    const startNum = 0;
    const endNum = minCellNumber;
    setSliceStart(startNum);
    setSliceEnd(endNum);
    setCurrentPage(1);
  }, [firstPageFlg, totalPage]);

  useEffect(() => {
    isFirstRender.current = false;
    // 親コンポーネントにpage番号を渡す
    onChange(currentPage);
    setDisabledPrev(false);
    setDisabledNext(false);

    // 最後のページに移動したときは「→」を無効にする
    // 最後のページから移動するときは「←」を有効にする
    if (currentPage === totalPage) {
      setDisabledPrev(false);
      setDisabledNext(true);
    }
    // 最初のページに戻った時は「←」を無効にする
    // 最初のページから移動するときは「←」を有効にする
    if (currentPage === 1) {
      setDisabledPrev(true);
      setDisabledNext(false);
    }
    // 1ページしかないときは「←」「→」は無効
    if (totalPage === 1) {
      setDisabledPrev(true);
      setDisabledNext(true);
    }
  }, [currentPage, onChange, totalPage]);

  // 「←」がクリックされたときの処理
  const handlePrevious = () => {
    const startNum = currentPage > Math.round(minCellNumber/2)
      ? currentPage < totalPage - Math.floor(minCellNumber/2)
        ? currentPage - Math.floor(minCellNumber/2) - 2
        : totalPage - minCellNumber
      : 0;
    const endNum = currentPage > Math.round(minCellNumber/2)
      ? currentPage < totalPage - Math.floor(minCellNumber/2)
        ? currentPage + Math.round(minCellNumber/2) - 2
        : totalPage
      : minCellNumber;
    setSliceStart(startNum);
    setSliceEnd(endNum);
    setCurrentPage(currentPage - 1);
  };

  // 「→」がクリックされたときの処理
  const handleNext = () => {
    const startNum = currentPage > Math.floor(minCellNumber/2)
      ? currentPage < totalPage - Math.floor(minCellNumber/2)
        ? currentPage - Math.floor(minCellNumber/2)
        : totalPage - minCellNumber
      : 0;
    const endNum = currentPage > Math.floor(minCellNumber/2)
      ? currentPage < totalPage - Math.floor(minCellNumber/2)
        ? currentPage + Math.round(minCellNumber/2)
        : totalPage
      : minCellNumber;
    setSliceStart(startNum);
    setSliceEnd(endNum);
    setCurrentPage(currentPage + 1);
  };

  // ページ番号を直接クリックされたときの処理
  const handleMove = (page: number): void => {
    if((page === 1) && (page === totalPage)){
      setDisabledPrev(true);
      setDisabledNext(true);
    } else if (page === 1) {
      setDisabledPrev(true);
      setDisabledNext(false);
    } else if (page === totalPage) {
      setDisabledNext(true);
      setDisabledPrev(false);
    } else {
      if (disabledPrev) setDisabledPrev(false);
      if (disabledNext) setDisabledNext(false);
    }

    const startNum = page > Math.floor(minCellNumber/2)
      ? page < totalPage - Math.floor(minCellNumber/2)
        ? page - Math.floor(minCellNumber/2) - 1
        : totalPage - minCellNumber
      : 0;
    const endNum = page > Math.floor(minCellNumber/2)
      ? page < totalPage - Math.floor(minCellNumber/2)
        ? page + Math.round(minCellNumber/2) - 1
        : totalPage
      : minCellNumber;
    setSliceStart(startNum);
    setSliceEnd(endNum);

    setCurrentPage(page);
  };

  return (
    <div className={`flex ${className}`}>
      <nav
        className="isolate inline-flex -space-x-px rounded-md shadow-sm select-none"
        aria-label="Pagination"
      >
        <div
          onClick={!disabledPrev ? handlePrevious : () =>{}}
          className={`${style.base} rounded-l-md ${!disabledPrev && 'cursor-pointer'}`}
        >
          <span className="sr-only">Previous</span>
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
        </div>
        {[...Array(totalPage).keys()].map((page) => {
          page++;
          return (
            <div
              key={`page-${page}`}
              aria-current="page"
              onClick={() => handleMove(page)}
              className={`${page === currentPage ? style.color[baseColor] : 'cursor-pointer'} relative z-10 inline-flex items-center border px-4 py-2 text-sm font-medium focus:z-20`}
            >
              {page}
            </div>
          );
        }).slice(sliceStart, sliceEnd)}

        <div
          onClick={!disabledNext ? handleNext : () => {}}
          className={`${style.base} rounded-r-md ${!disabledNext && 'cursor-pointer'}`}
        >
          <span className="sr-only">Next</span>
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
        </div>
      </nav>
    </div>
  );
};
